import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Container, Flex, LoadingIndicator } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';
import { isEmpty } from 'ramda';

import getCheapestFlightOption from '@shared/utils/getCheapestFlightOption/getCheapestFlightOption';

import FullPageModal from '@/components/FullPageModal';
import FullPageModalBody from '@/components/FullPageModal/components/Body';
import Text from '@/components/Text';
import { FLIGHT_DIRECTION } from '@/constants';
import useCampaign from '@/hooks/useCampaign';
import { getOfferCharges } from '@/hooks/useGetOfferCharges/useGetOfferCharges';
import useSearchQuery from '@/hooks/useSearchQuery';
import { longDate } from '@/lib/date/format';
import Charges from '@/propTypes/Charges';
import FlightOptionsPropTypes from '@/propTypes/FlightOptions';
import isPriceForAdult from '@/utils/campaigns/isPriceForAdult';
import getSelectedFlightOptionByQuery from '@/utils/getSelectedFlightOptionByQuery/getSelectedFlightOptionByQuery';
import isCampaignHidePerPersonPrice from '@/utils/isCampaignHidePerPersonPrice';
import { useFlightModal } from '@/v2/components/pages/PackageViewV2/contexts/FlightModalContext';
import { flightConfirmedDataLayerEvent } from '@/v2/components/pages/PackageViewV2/utils/dataLayer';
import DirectionTabs from './components/DirectionTabs';
import FlightList from './components/FlightList';
import FlightSummary from './components/FlightSummary';

const FlightOptions = React.memo(
  ({ loading = false, flightOptions, bestOfferCharges, onConfirmFlights }) => {
    const { searchQuery } = useSearchQuery();

    const flightListRef = useRef(null);

    const [selectedOutboundOption, setOutboundOption] = useState();
    const [selectedInboundOption, setInboundOption] = useState();
    const [direction, setDirection] = useState(FLIGHT_DIRECTION.Departing);
    const { closeFlightModal } = useFlightModal();

    useEffect(() => {
      if (flightOptions && !selectedOutboundOption && !selectedInboundOption) {
        const { preSelectedOutbound, preSelectedInbound } =
          getSelectedFlightOptionByQuery(flightOptions, searchQuery);
        const { cheapestOutbound, cheapestInbound } =
          getCheapestFlightOption(flightOptions);

        const selectedOption = {
          outbound: preSelectedOutbound || cheapestOutbound,
          inbound: preSelectedInbound || cheapestInbound,
        };

        setOutboundOption(selectedOption.outbound);
        setInboundOption(selectedOption.inbound);
      }
    }, [
      flightOptions,
      selectedOutboundOption,
      selectedInboundOption,
      searchQuery,
    ]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const segments = {
      outbound: selectedOutboundOption?.flightSegments || [],
      inbound: selectedInboundOption?.flightSegments || [],
    };

    const { priceForAdult, hidePriceSubText } = useMemo(
      () => isPriceForAdult(segments),
      [segments],
    );

    const scrollToFlightList = useCallback(() => {
      if (typeof flightListRef?.current?.scroll !== 'function') return;

      flightListRef?.current?.scroll({
        top: 0,
      });
    }, [flightListRef]);

    const setActiveTabWithScroll = (args) => {
      scrollToFlightList();
      setDirection(args);
    };

    const displayRouteText = (options, tab) => {
      if (!options) return null;
      const { flightSegments } = options.outbound[0];
      const { origin } = flightSegments[0];
      const { destination } = flightSegments[flightSegments.length - 1];

      return tab === FLIGHT_DIRECTION.Departing
        ? `${origin.name} (${origin.code}) to ${destination.name} (${destination.code})`
        : `${destination.name} (${destination.code}) to ${origin.name} (${origin.code})`;
    };

    const onClose = () => {
      closeFlightModal();
    };

    const onConfirm = () => {
      if (direction === FLIGHT_DIRECTION.Departing) {
        // Analytics event
        const departingIndex = flightOptions?.outbound?.findIndex(
          (option) => option === selectedOutboundOption,
        );
        if (departingIndex >= 0) {
          flightConfirmedDataLayerEvent({
            flightDirection: 'Departing',
            flightIndex: departingIndex + 1,
          });
        }

        setOutboundOption(selectedOutboundOption);
        setDirection(FLIGHT_DIRECTION.Returning);
        scrollToFlightList();
      } else {
        // Analytics event
        const returningIndex = flightOptions?.inbound?.findIndex(
          (option) => option === selectedInboundOption,
        );
        if (returningIndex >= 0) {
          flightConfirmedDataLayerEvent({
            flightDirection: 'Returning',
            flightIndex: returningIndex + 1,
          });
        }

        setOutboundOption(selectedOutboundOption);
        setInboundOption(selectedInboundOption);
        onConfirmFlights({ segments });
      }
    };

    const offerCharges = useMemo(() => {
      return (
        bestOfferCharges &&
        selectedInboundOption &&
        selectedOutboundOption &&
        getOfferCharges({
          offer: {
            charges: bestOfferCharges,
          },
          selectedInboundOption,
          selectedOutboundOption,
        })
      );
    }, [bestOfferCharges, selectedInboundOption, selectedOutboundOption]);

    const campaign = useCampaign();
    const hidePerPersonPrice = isCampaignHidePerPersonPrice(campaign);

    if (!selectedOutboundOption || !selectedInboundOption) {
      return null;
    }

    return (
      !isEmpty(searchQuery) && (
        <FullPageModal onClose={onClose}>
          <FullPageModal.Header>Flights selection</FullPageModal.Header>
          <FullPageModalBody bg="background.page" ref={flightListRef}>
            <DirectionTabs
              loading={loading}
              activeTab={direction}
              onTabClicked={setActiveTabWithScroll}
            />
            <Box
              bg="background.card"
              textAlign="center"
              p="4"
              borderBottom="1px solid"
              borderColor="border"
              top="53px"
              left="0"
              right="0"
              position="sticky"
              zIndex="popup"
            >
              <Text pb={2} bold medium>
                {displayRouteText(flightOptions, direction)}
              </Text>
              <Text display="block" medium>
                {direction === FLIGHT_DIRECTION.Departing
                  ? longDate(searchQuery.departureDate)
                  : longDate(searchQuery.returnDate)}
              </Text>
            </Box>
            {loading ? (
              <Flex
                mt="10"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                <Box mb="4">We’re loading your flight options</Box>
                <LoadingIndicator />
              </Flex>
            ) : (
              <>
                <Box data-testid="FLIGHT-OPTION-LIST" py="4">
                  <Container px="4" mx={['0', null, 'auto']}>
                    <FlightList
                      selectedFlightOption={{
                        outbound: selectedOutboundOption,
                        inbound: selectedInboundOption,
                      }}
                      setSelectedFlightOption={{
                        setOutbound: setOutboundOption,
                        setInbound: setInboundOption,
                      }}
                      flightDirection={direction}
                      flightOptions={flightOptions}
                      hidePerPersonPrice={hidePerPersonPrice}
                    />
                  </Container>
                </Box>
                {offerCharges && (
                  <FlightSummary
                    charges={offerCharges}
                    onClicked={onConfirm}
                    priceForAdult={priceForAdult}
                    hidePerPersonPrice={hidePerPersonPrice}
                    hidePriceSubText={hidePriceSubText}
                    direction={direction}
                  />
                )}
              </>
            )}
          </FullPageModalBody>
        </FullPageModal>
      )
    );
  },
);

FlightOptions.propTypes = {
  loading: PropTypes.bool,
  flightOptions: PropTypes.shape(FlightOptionsPropTypes),
  bestOfferCharges: PropTypes.shape(Charges),
};

export default FlightOptions;
